import {
    type CSSProperties,
    useLayoutEffect,
    type FocusEvent,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    SearchKitIcon
} from "./kitIcons";
import {
    getFocusRingStyle,
    useReducedMotion,
    NCoreUIKitTheme,
    getCornerStyle,
    AnimatedIcon,
    Collapsible,
    TextInput,
    Badge,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    useDocsRouter,
    DocsLink
} from "../router";
import {
    ChevronRight,
    ChevronDown
} from "lucide-react-native";
import {
    type DocsNavItem,
    findGroupOfPath,
    BLOCKS_NAV,
    CREATE_NAV,
    DOCS_NAV
} from "../nav";

type SidebarProps = {
    onNavigate: () => void;
    isOpen: boolean;
};

const normalizeSearchText = (value: string, language: string) => value.toLocaleLowerCase(language).normalize("NFD").replace(/\p{M}/gu, "").replace(/ı/g, "i");

const stylesheet = StyleSheet.create({
    section: {
        textTransform: "uppercase",
        letterSpacing: 0.72
    },
    badge: {
        alignSelf: "center",
        flexShrink: 0
    },
    index: {
        textAlign: "right",
        fontVariant: [
            "tabular-nums"
        ],
        flexShrink: 0
    },
    count: {
        fontVariant: [
            "tabular-nums"
        ],
        marginLeft: "auto"
    },
    label: {
        flexShrink: 1,
        flexGrow: 1,
        minWidth: 0
    }
});

const domStyles: Record<string, CSSProperties> = {
    list: {
        flexDirection: "column",
        listStyle: "none",
        display: "flex",
        padding: 0,
        margin: 0
    },
    row: {
        textDecoration: "none",
        alignItems: "baseline",
        display: "flex"
    },
    header: {
        alignItems: "center",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        width: "100%",
        border: 0
    }
};

const useRowState = () => {
    const [
        isHovered,
        setIsHovered
    ] = useState(false);

    const [
        isFocusVisible,
        setIsFocusVisible
    ] = useState(false);

    return {
        onFocus: (event: FocusEvent<HTMLElement>) => setIsFocusVisible(event.currentTarget.matches(":focus-visible")),
        onMouseLeave: () => setIsHovered(false),
        onBlur: () => setIsFocusVisible(false),
        onMouseEnter: () => setIsHovered(true),
        isFocusVisible,
        isHovered
    };
};

const useTopOffset = () => {
    const [
        topOffset,
        setTopOffset
    ] = useState(0);

    useLayoutEffect(() => {
        const header = document.querySelector("header");

        if(!header) {
            return undefined;
        }

        const measure = () => setTopOffset(header.getBoundingClientRect().height);

        const observer = new ResizeObserver(measure);

        measure();

        observer.observe(header);

        return () => {
            observer.disconnect();
        };
    }, []);

    return topOffset;
};

const NavLink = ({
    isNumbered,
    onNavigate,
    isCurrent,
    item,
    t
}: {
    t: (key: string) => string;
    onNavigate: () => void;
    isNumbered: boolean;
    isCurrent: boolean;
    item: DocsNavItem;
}) => {
    const {
        controlPaddings,
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const rowState = useRowState();

    const {
        isFocusVisible,
        isHovered
    } = rowState;

    let background = "transparent";

    if(isCurrent) {
        background = colors.content.container.selected;
    } else if(isHovered) {
        background = colors.system.state.overlay.hover.default;
    }

    return <li
        key={item.path}
    >
        <DocsLink
            aria-current={isCurrent ? "page" : undefined}
            style={{
                ...domStyles.row,
                ...getFocusRingStyle({
                    isFocusVisible,
                    borders,
                    colors
                }) as CSSProperties,
                paddingRight: controlPaddings.small,
                paddingLeft: controlPaddings.small,
                paddingBottom: spaces.spacingSm,
                ...getCornerStyle({
                    radius: radiuses.actions,
                    shapeConfigs
                }) as CSSProperties,
                paddingTop: spaces.spacingSm,
                gap: spaces.spacingSm,
                background
            }}
            onMouseEnter={rowState.onMouseEnter}
            onMouseLeave={rowState.onMouseLeave}
            onFocus={rowState.onFocus}
            onBlur={rowState.onBlur}
            onClick={onNavigate}
            href={item.path}
        >
            {
                isNumbered ?
                    <Text
                        color={isCurrent ? "mid" : "low"}
                        style={[
                            stylesheet.index,
                            {
                                width: spaces.spacingXl
                            }
                        ]}
                        variant="monoMediumSize"
                        aria-hidden={true}
                    >
                        {String(item.index).padStart(2, "0")}
                    </Text>
                :
                    null
            }
            <Text
                variant={isCurrent ? "labelMediumSize" : "bodyMediumSize"}
                color={isCurrent ? "high" : "mid"}
                style={stylesheet.label}
                numberOfLines={1}
            >
                {item.title ?? t(item.titleKey ?? "")}
            </Text>
            {
                item.isNew ?
                    <View
                        style={stylesheet.badge}
                    >
                        <Badge
                            title={t("nav.new")}
                            variant="tonal"
                            type="neutral"
                            size="small"
                        />
                    </View>
                :
                    null
            }
            {
                item.isDeprecated ?
                    <View
                        style={stylesheet.badge}
                    >
                        <Badge
                            title={t("nav.deprecated")}
                            variant="tonal"
                            type="warning"
                            size="small"
                        />
                    </View>
                :
                    null
            }
        </DocsLink>
    </li>;
};

const GroupHeader = ({
    isExpanded,
    onToggle,
    count,
    title
}: {
    onToggle: () => void;
    isExpanded: boolean;
    count: number;
    title: string;
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const rowState = useRowState();

    const {
        isFocusVisible,
        isHovered
    } = rowState;

    return <button
        style={{
            ...domStyles.header,
            ...getFocusRingStyle({
                isFocusVisible,
                borders,
                colors
            }) as CSSProperties,
            background: isHovered ? colors.system.state.overlay.hover.default : "transparent",
            paddingRight: controlPaddings.small,
            paddingLeft: controlPaddings.small,
            minHeight: controlHeights.small,
            paddingBottom: spaces.spacingXs,
            ...getCornerStyle({
                radius: radiuses.actions,
                shapeConfigs
            }) as CSSProperties,
            paddingTop: spaces.spacingXs,
            gap: spaces.spacingSm
        }}
        onMouseEnter={rowState.onMouseEnter}
        onMouseLeave={rowState.onMouseLeave}
        onFocus={rowState.onFocus}
        aria-expanded={isExpanded}
        onBlur={rowState.onBlur}
        onClick={onToggle}
        type="button"
    >
        <AnimatedIcon
            isTransitioned={isExpanded}
            fromIcon={ChevronRight}
            iconTransition="morph"
            toIcon={ChevronDown}
            color="low"
            size={16}
        />
        <Text
            variant="titleSmallSize"
            color="high"
        >
            {title}
        </Text>
        <Text
            style={stylesheet.count}
            variant="monoMediumSize"
            color="low"
        >
            {count}
        </Text>
    </button>;
};

const Sidebar = ({
    onNavigate,
    isOpen
}: SidebarProps) => {
    const {
        language,
        t
    } = useDocsI18n();

    const {
        path
    } = useDocsRouter();

    const {
        shapeConfigs,
        durations,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const isReduceMotionEnabled = useReducedMotion();

    const topOffset = useTopOffset();

    const [
        isScrollbarVisible,
        setIsScrollbarVisible
    ] = useState(false);

    const [
        query,
        setQuery
    ] = useState("");

    const [
        expandedGroups,
        setExpandedGroups
    ] = useState<Set<string>>(() => new Set([findGroupOfPath(path)?.id ?? "basic"]));

    useEffect(() => {
        const activeGroup = findGroupOfPath(path);

        if(!activeGroup) {
            return;
        }

        setExpandedGroups((current) => {
            if(current.has(activeGroup.id)) {
                return current;
            }

            const next = new Set(current);

            next.add(activeGroup.id);

            return next;
        });
    }, [
        path
    ]);

    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            const activeLink = document.querySelector<HTMLElement>("#docs-sidebar a[aria-current=\"page\"]");

            activeLink?.scrollIntoView({
                block: "nearest"
            });
        });

        return () => {
            cancelAnimationFrame(frame);
        };
    }, [
        path
    ]);

    const normalizedQuery = normalizeSearchText(query.trim(), language);

    const getItemTitle = (item: DocsNavItem) => item.title ?? t(item.titleKey ?? "");

    const isMatch = (item: DocsNavItem) => !normalizedQuery || normalizeSearchText(getItemTitle(item), language).includes(normalizedQuery);

    const handleGroupExpandedChange = (groupId: string, isExpanded: boolean) => {
        setExpandedGroups((current) => {
            const next = new Set(current);

            if(isExpanded) {
                next.add(groupId);
            } else {
                next.delete(groupId);
            }

            return next;
        });
    };

    const renderItem = (item: DocsNavItem, isNumbered: boolean) => <NavLink
        isCurrent={item.path === path}
        onNavigate={onNavigate}
        isNumbered={isNumbered}
        key={item.path}
        item={item}
        t={t}
    />;

    const isCompactScreen = width < shapeConfigs.compactBreakpoint;

    const navSections = isCompactScreen ? [
        ...DOCS_NAV,
        BLOCKS_NAV,
        CREATE_NAV
    ] : DOCS_NAV;

    const sections = navSections.map((section) => ({
        ...section,
        groups: section.groups.map((group) => ({
            ...group,
            visibleItems: group.items.filter(isMatch)
        })).filter((group) => group.visibleItems.length > 0)
    })).filter((section) => section.groups.length > 0);

    const isDrawer = width < shapeConfigs.wideBreakpoint;

    const duration = isReduceMotionEnabled ? 0 : durations.medium;

    let navStyle: CSSProperties = {
        scrollbarColor: isScrollbarVisible ? `${colors.content.border.subtle} transparent` : "transparent transparent",
        paddingBottom: spaces.spacingXxl,
        paddingRight: spaces.spacingMd,
        paddingLeft: spaces.spacingLg,
        overscrollBehavior: "contain",
        paddingTop: spaces.spacingLg,
        scrollbarGutter: "stable",
        boxSizing: "border-box",
        scrollbarWidth: "thin",
        overflowY: "auto",
        top: topOffset
    };

    if(isDrawer) {
        navStyle = {
            ...navStyle,
            transition: `transform ${duration}ms ease, visibility 0s linear ${isOpen ? 0 : duration}ms`,
            borderRight: `${borders.line}px solid ${colors.content.border.subtle}`,
            transform: isOpen ? "none" : "translateX(-100%)",
            background: colors.content.container.default,
            visibility: isOpen ? "visible" : "hidden",
            zIndex: shapeConfigs.layers.overlay,
            width: shapeConfigs.drawerWidth,
            position: "fixed",
            bottom: 0,
            left: 0
        };
    } else {
        navStyle = {
            ...navStyle,
            height: `calc(100vh - ${topOffset}px)`,
            position: "sticky"
        };
    }

    return <nav
        onMouseLeave={() => setIsScrollbarVisible(false)}
        onMouseEnter={() => setIsScrollbarVisible(true)}
        onFocus={() => setIsScrollbarVisible(true)}
        onBlur={() => setIsScrollbarVisible(false)}
        data-open={isOpen ? "true" : "false"}
        aria-label={t("nav.label")}
        id="docs-sidebar"
        style={navStyle}
    >
        <div
            style={{
                marginBottom: spaces.spacingLg
            }}
        >
            <TextInput
                accessibilityLabel={t("nav.filter")}
                placeholder={t("nav.filter")}
                spreadBehaviour="stretch"
                onChangeText={setQuery}
                isClearEnabled={true}
                icon={SearchKitIcon}
                value={query}
                size="small"
            />
        </div>
        {
            sections.length === 0 ?
                <div
                    style={{
                        padding: spaces.spacingSm
                    }}
                    role="status"
                >
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("nav.empty")}
                    </Text>
                </div>
            :
                null
        }
        {
            sections.map((section) => <div
                key={section.id}
            >
                <div
                    style={{
                        padding: `${spaces.spacingMd}px ${spaces.spacingSm}px ${spaces.spacingSm}px`,
                        marginBottom: spaces.spacingSm,
                        display: "flex"
                    }}
                >
                    <Text
                        style={stylesheet.section}
                        variant="labelSmallSize"
                        aria-level={2}
                        role="heading"
                        color="low"
                    >
                        {t(section.titleKey)}
                    </Text>
                </div>
                {
                    section.groups.map((group) => {
                        if(!section.isCollapsible) {
                            return <ul
                                style={{
                                    ...domStyles.list,
                                    marginBottom: spaces.spacingMd,
                                    gap: spaces.spacingXs
                                }}
                                key={group.id}
                            >
                                {group.visibleItems.map((item) => renderItem(item, false))}
                            </ul>;
                        }

                        const isExpanded = normalizedQuery.length > 0 || expandedGroups.has(group.id);

                        return <Collapsible
                            onOpenChange={(isGroupOpen) => handleGroupExpandedChange(group.id, isGroupOpen)}
                            isOpen={isExpanded}
                            key={group.id}
                        >
                            <GroupHeader
                                onToggle={() => handleGroupExpandedChange(group.id, !isExpanded)}
                                count={group.visibleItems.length}
                                title={t(group.titleKey)}
                                isExpanded={isExpanded}
                            />
                            <Collapsible.Content>
                                <ul
                                    style={{
                                        ...domStyles.list,
                                        marginBottom: spaces.spacingMd,
                                        gap: spaces.spacingXs
                                    }}
                                >
                                    {group.visibleItems.map((item) => renderItem(item, true))}
                                </ul>
                            </Collapsible.Content>
                        </Collapsible>;
                    })
                }
            </div>)
        }
    </nav>;
};
export default Sidebar;
